import { cx } from '@orn/shared-web/utils/cx';

import JsonView from '../../components/JsonView';

import MetricChip from './transcript/MetricChip';
import { AUDIT_PARAM_COLOR_CLASSES, formatDurationMs, ROLE_ACCENT } from './transcript/transcriptModel';
import {
  roundStats,
  thinkingDisplayText,
  toolResultMeta,
  type IRoundStat,
  type RoundStatTone,
} from './transcriptModalView';

import type {
  IMopsAssistantBenchmarkCheckV1,
  IMopsAssistantBenchmarkIterationTranscriptV1,
  IMopsAssistantBenchmarkRoundTranscriptV1,
  IMopsAssistantBenchmarkTranscriptEventV1,
} from '@orn/global/assistant/MopsAssistantBenchmarkArtifact.js';

/** Per-round stat chip color, by the view-model's semantic tone. */
const ROUND_STAT_TONE_CLASS: Record<RoundStatTone, string> = {
  money: 'text-fg-success-primary', // cost — money green
  muted: 'text-fg-quaternary', // cached read — cheap, dim
  neutral: 'text-fg-tertiary', // total footprint
  accent: 'text-fg-primary', // generated output — the number that varies
  latency: 'text-fg-brand-primary', // model latency
  thinking: 'text-fg-warning-primary', // thinking time
  warn: 'text-fg-error-primary font-bold', // pathologically slow thinking
  none: 'text-fg-quaternary', // unmeasured
};

/** Hover definition per stat chip, so the terse labels stay legible. */
const ROUND_STAT_TITLE: Record<IRoundStat['key'], string> = {
  cost: 'Model cost for this round (USD)',
  cached: 'Cache-read tokens — the warm prompt prefix served from cache',
  total: 'Total tokens the round touched (uncached input + cache read/write + output)',
  out: 'Output tokens generated this round (includes reasoning)',
  lat: 'Model latency — wall-clock to produce this round',
  think: 'Of the latency, time spent thinking (extended reasoning); red past the slow-thinking bar',
};

/** Thread-transcript accents reused so both transcript tools share one color language. */
const EVENT_ACCENT = {
  text: ROLE_ACCENT.assistant, // green
  thinking: ROLE_ACCENT.system_instruction, // dim
  tool: ROLE_ACCENT.toolResult, // amber
} as const;

interface IRoundStatLineProps {
  round: IMopsAssistantBenchmarkRoundTranscriptV1;
}

/** The round's cost/token/latency chips — fixed-width so they align down the transcript. */
const RoundStatLine = ({ round }: IRoundStatLineProps) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 font-mono text-[10px] tabular-nums">
    {roundStats(round).map((stat) => (
      <span key={stat.key} title={ROUND_STAT_TITLE[stat.key]}>
        <MetricChip
          label={stat.label}
          value={stat.value}
          valueClass={ROUND_STAT_TONE_CLASS[stat.tone]}
          labelWidthClass="w-[5ch]"
          valueWidthClass="w-[9ch]"
        />
      </span>
    ))}
  </div>
);

interface IEventRowProps {
  event: IMopsAssistantBenchmarkTranscriptEventV1;
}

/** One LLM event — assistant text, its thinking, or a tool call + result. */
const EventRow = ({ event }: IEventRowProps) => {
  if (event.t === 'text') {
    return (
      <div className={cx('border-l-2 pl-2', EVENT_ACCENT.text.border)}>
        <div className={cx('text-[9px] uppercase tracking-wider', EVENT_ACCENT.text.label)}>text</div>
        <div className="text-fg-primary whitespace-pre-wrap text-[11px] leading-snug">{event.text}</div>
      </div>
    );
  }

  if (event.t === 'thinking') {
    return (
      <div className={cx('border-l-2 pl-2', EVENT_ACCENT.thinking.border)}>
        <div className={cx('text-[9px] uppercase tracking-wider', EVENT_ACCENT.thinking.label)}>
          {event.redacted ? 'thinking · redacted' : 'thinking'}
        </div>
        <div
          className={cx(
            'whitespace-pre-wrap text-[11px] italic leading-snug',
            event.redacted ? 'text-fg-quaternary' : 'text-fg-tertiary'
          )}
        >
          {thinkingDisplayText(event.text, event.redacted)}
        </div>
      </div>
    );
  }

  return (
    <div className={cx('border-l-2 pl-2', EVENT_ACCENT.tool.border)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={cx('text-[9px] uppercase tracking-wider', EVENT_ACCENT.tool.label)}>tool</span>
        <span className="font-mono text-[11px] font-semibold text-fuchsia-400">{event.name}</span>
        {event.isError && (
          <span className="border-fg-error-primary/50 text-fg-error-primary rounded-sm border px-1 text-[9px] font-semibold uppercase">
            error
          </span>
        )}
        {event.ms !== null && (
          <span className="text-fg-quaternary font-mono text-[10px] tabular-nums">{formatDurationMs(event.ms)}</span>
        )}
      </div>
      <div className="mt-0.5 whitespace-pre-wrap font-mono text-[11px] leading-snug">
        <JsonView value={event.args} stringKeyClasses={AUDIT_PARAM_COLOR_CLASSES} alignValues />
      </div>
      <div className="mt-1">
        <div className="text-fg-quaternary text-[9px] uppercase tracking-wider">
          result · {toolResultMeta(event.resultBytes, event.truncated)}
        </div>
        <pre
          className={cx(
            'whitespace-pre-wrap break-all font-mono text-[10px] leading-snug',
            event.isError ? 'text-fg-error-primary' : 'text-fg-secondary'
          )}
        >
          {event.result}
        </pre>
      </div>
    </div>
  );
};

interface IRoundBlockProps {
  round: IMopsAssistantBenchmarkRoundTranscriptV1;
}

/** One LLM round: its stat line, then the events it produced. */
const RoundBlock = ({ round }: IRoundBlockProps) => (
  <div className="border-border-primary/40 border-b px-3 py-2">
    <div className="mb-1.5 flex items-baseline gap-2">
      <span className="text-fg-quaternary shrink-0 font-mono text-[10px]">R{round.round}</span>
      <RoundStatLine round={round} />
    </div>
    <div className="flex flex-col gap-1.5">
      {round.events.map((event, i) => (
        <EventRow key={i} event={event} />
      ))}
    </div>
  </div>
);

interface IChecksBlockProps {
  checks: IMopsAssistantBenchmarkCheckV1[];
}

/**
 * The assertions evaluated for this run — the "why" behind its pass/fail — at the
 * foot of the transcript. A failed check shows its detail (expected vs actual),
 * so an assertion failure reads as more than a bare red badge.
 */
const ChecksBlock = ({ checks }: IChecksBlockProps) => {
  const passed = checks.filter((check) => check.passed).length;
  return (
    <section className="border-border-primary bg-bg-secondary/30 border-t px-3 py-2">
      <div className="text-fg-tertiary mb-1.5 text-[9px] font-semibold uppercase tracking-wider">
        Checks · {passed}/{checks.length} passed
      </div>
      <div className="flex flex-col gap-1.5">
        {checks.map((check, i) => (
          <div key={i} className="flex gap-2 text-[11px] leading-snug">
            <span
              aria-hidden
              className={cx('shrink-0 font-mono', check.passed ? 'text-fg-success-primary' : 'text-fg-error-primary')}
            >
              {check.passed ? '✓' : '✗'}
            </span>
            <div className="min-w-0">
              <span className={check.passed ? 'text-fg-secondary' : 'text-fg-primary'}>{check.description}</span>
              {check.detail !== null && <div className="text-fg-error-primary mt-0.5 text-[10px]">{check.detail}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

interface IBenchmarkTranscriptPaneProps {
  iteration: IMopsAssistantBenchmarkIterationTranscriptV1;
}

/**
 * The scrollable transcript for one captured run: its turns, each turn's opening
 * prompt in a sticky header, then the LLM rounds it drove — every round carrying
 * its per-round cost/token/latency stat line above its events — and, at the foot,
 * the checks that explain the run's pass/fail.
 */
const BenchmarkTranscriptPane = ({ iteration }: IBenchmarkTranscriptPaneProps) => (
  <div className="flex flex-col">
    {iteration.turns.map((turn) => (
      <section key={turn.turnIndex}>
        <div className="border-border-primary bg-bg-secondary sticky top-0 z-10 border-b px-3 py-2">
          <div className="text-fg-brand-primary text-[9px] font-semibold uppercase tracking-wider">
            Turn {turn.turnIndex} · prompt
          </div>
          <div className="text-fg-primary whitespace-pre-wrap font-mono text-[11px] leading-snug">{turn.prompt}</div>
        </div>
        {turn.rounds.map((round) => (
          <RoundBlock key={round.round} round={round} />
        ))}
      </section>
    ))}
    {iteration.checks !== undefined && iteration.checks.length > 0 && <ChecksBlock checks={iteration.checks} />}
  </div>
);

export default BenchmarkTranscriptPane;
